import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Globe, CheckCircle, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';
import { getStoredVisitorId, getStoredUtm } from '../hooks/useVisitTracker';

export default function RegisterPage() {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState(searchParams.get('url') || '');
  const [form, setForm] = useState({ naam: '', email: searchParams.get('email') || '', wachtwoord: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [akkoord, setAkkoord] = useState(false);

  const pwStrength = form.wachtwoord.length === 0 ? 0 : form.wachtwoord.length < 8 ? 1 : form.wachtwoord.length < 12 ? 2 : 3;
  const pwLabels = ['', 'Zwak', 'Gemiddeld', 'Sterk'];
  const pwColors = ['', '#ef4444', '#f59e0b', '#22c55e'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!akkoord) { toast.error('Ga akkoord met de voorwaarden om door te gaan.'); return; }
    if (form.wachtwoord.length < 8) { toast.error('Wachtwoord moet minimaal 8 tekens zijn.'); return; }

    setLoading(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        naam: form.naam,
        email: form.email,
        wachtwoord: form.wachtwoord,
        website_url: websiteUrl,
        visitor_id: getStoredVisitorId(),
        ...Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].map(key => [key, searchParams.get(key) || getStoredUtm()[key] || ''])),
      });
      localStorage.setItem('portal_token', r.data.token);
      await refreshUser();
      navigate('/onboarding');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registratie mislukt. Probeer opnieuw.';
      toast.error(msg);
    }
    setLoading(false);
  };

  const canProceed = websiteUrl.trim().length > 5 && (websiteUrl.includes('.') || websiteUrl.includes('http'));

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#f8fafc' }}>
      <SEOHead title="Gratis groeiscan" description="Start uw gratis websiteanalyse met IMPERIA AI en ontdek uw online groeikansen." canonical="/register" />
      {/* Left panel */}
      <div style={{
        width: '420px', flexShrink: 0, background: 'linear-gradient(180deg, #0f172a, #1e1b4b)',
        padding: '40px 32px', flexDirection: 'column', justifyContent: 'space-between',
      }} className="hidden lg:flex">
        <div>
          <Link to="/" data-testid="register-desktop-brand-link" style={{ textDecoration: 'none' }}>
            <h1 data-testid="register-desktop-brand" style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>
              IMPERIA <span style={{ color: '#D4A017' }}>AI</span>
            </h1>
            <p data-testid="register-brand-tagline" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>AI Marketing Manager</p>
          </Link>
          <div style={{ marginTop: '48px' }}>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                {step === 1 ? (
                  <>
                    <Globe size={36} style={{ color: '#D4A017', marginBottom: '16px' }} />
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: 'white', marginBottom: '8px' }}>
                      Ontdek uw groeikansen
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6 }}>
                      Voer uw website in en ontvang binnen 2 minuten een gratis analyse met concrete verbeterpunten.
                    </p>
                  </>
                ) : (
                  <>
                    <CheckCircle size={36} style={{ color: '#D4A017', marginBottom: '16px' }} />
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: 'white', marginBottom: '8px' }}>
                      Bijna klaar!
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6 }}>
                      Maak een account aan om uw persoonlijke dashboard en gratis scan resultaten te bewaren.
                    </p>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {['Gratis website analyse', 'Concrete verbeterpunten', 'Concurrent vergelijking', 'Persoonlijk dashboard'].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle size={14} style={{ color: '#D4A017' }} />
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '11px' }}>Geen creditcard nodig · Altijd gratis starten</p>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ width: '100%', maxWidth: '440px' }}>
          <Link to="/" data-testid="register-mobile-brand-link" className="lg:hidden" style={{ fontFamily: "'Sora', sans-serif", color: '#0f172a', fontWeight: 800, textDecoration: 'none' }}>IMPERIA <span style={{ color: '#D4A017' }}>AI</span></Link>

          {/* Progress */}
          <div data-testid="register-step-progress" aria-label={`Stap ${step} van 2`} style={{ display: 'flex', gap: '6px', marginTop: '20px', marginBottom: '28px' }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                flex: 1, height: '4px', borderRadius: '2px',
                background: step >= s ? '#D4A017' : '#e2e8f0', transition: 'background 0.3s',
              }} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                  Start uw gratis groeiscan
                </h2>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '28px' }}>
                  Voer uw website URL in om te beginnen
                </p>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Website URL</label>
                  <div style={{ position: 'relative' }}>
                    <Globe size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      data-testid="website-url-input"
                      type="url"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      placeholder="https://uwbedrijf.nl"
                      autoFocus
                      style={{
                        width: '100%', padding: '14px 14px 14px 40px', borderRadius: '12px',
                        border: '2px solid #e2e8f0', fontSize: '15px', color: '#0f172a',
                        outline: 'none', transition: 'border 0.2s', background: 'white',
                      }}
                      onFocus={e => e.target.style.borderColor = '#D4A017'}
                      onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                    Wij analyseren uw website op SEO, snelheid, content en meer
                  </p>
                </div>

                <button
                  data-testid="website-next-btn"
                  onClick={() => canProceed && setStep(2)}
                  disabled={!canProceed}
                  style={{
                    width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                    background: canProceed ? 'linear-gradient(135deg, #0f172a, #1e1b4b)' : '#e2e8f0',
                    color: canProceed ? 'white' : '#94a3b8', fontSize: '15px', fontWeight: 700,
                    cursor: canProceed ? 'pointer' : 'not-allowed', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s',
                  }}
                >
                  Volgende <ArrowRight size={17} />
                </button>

                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', marginTop: '20px' }}>
                  Al een account? <Link to="/login" data-testid="register-login-link" style={{ color: '#D4A017', fontWeight: 600, textDecoration: 'none' }}>Inloggen</Link>
                </p>
              </motion.div>
            ) : (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <button data-testid="register-back-button" onClick={() => setStep(1)} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', background: 'none',
                  border: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer', marginBottom: '16px',
                }}>
                  <ArrowLeft size={14} /> Terug
                </button>

                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                  Maak uw account aan
                </h2>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>
                  Bewaar uw scan resultaten en ontvang uw persoonlijke dashboard
                </p>
                <div data-testid="register-selected-website" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f0fdf4',
                  padding: '4px 10px', borderRadius: '8px', fontSize: '12px', color: '#16a34a', fontWeight: 600, marginBottom: '20px', maxWidth: '100%', overflowWrap: 'anywhere',
                }}>
                  <Globe size={12} /> {websiteUrl}
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Naam</label>
                    <input
                      data-testid="register-name-input" type="text" value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })}
                      placeholder="Voor- en achternaam" required
                      className="rc-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>E-mailadres</label>
                    <input
                      data-testid="register-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="naam@bedrijf.nl" required
                      className="rc-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Wachtwoord</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="register-password-input" type={showPw ? 'text' : 'password'} value={form.wachtwoord}
                        onChange={e => setForm({ ...form, wachtwoord: e.target.value })}
                        placeholder="Minimaal 8 tekens" required
                        className="rc-input" style={{ paddingRight: '48px' }}
                      />
                      <button data-testid="register-password-visibility-button" aria-label={showPw ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'} type="button" onClick={() => setShowPw(!showPw)}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                        {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {form.wachtwoord && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                        <div style={{ flex: 1, height: '3px', borderRadius: '2px', background: '#e2e8f0' }}>
                          <div style={{ width: `${(pwStrength / 3) * 100}%`, height: '100%', borderRadius: '2px', background: pwColors[pwStrength], transition: 'all 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: pwColors[pwStrength], fontWeight: 600 }}>{pwLabels[pwStrength]}</span>
                      </div>
                    )}
                  </div>

                  <div data-testid="akkoord-checkbox" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                    <input
                      data-testid="register-terms-checkbox" type="checkbox" checked={akkoord} onChange={() => setAkkoord(!akkoord)}
                      style={{ width: '18px', height: '18px', accentColor: '#D4A017', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ color: '#64748b', fontSize: '12px', lineHeight: 1.5 }}>
                      Ik ga akkoord met de <a href="/voorwaarden" target="_blank" rel="noopener" style={{ color: '#D4A017' }} onClick={e => e.stopPropagation()}>algemene voorwaarden</a> en <a href="/privacy" target="_blank" rel="noopener" style={{ color: '#D4A017' }} onClick={e => e.stopPropagation()}>privacybeleid</a>
                    </span>
                  </div>

                  <button data-testid="register-submit-button" type="submit" disabled={loading || !akkoord}
                    style={{
                      width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                      background: (loading || !akkoord) ? '#e2e8f0' : 'linear-gradient(135deg, #0f172a, #1e1b4b)',
                      color: (loading || !akkoord) ? '#94a3b8' : 'white', fontSize: '15px', fontWeight: 700,
                      cursor: (loading || !akkoord) ? 'not-allowed' : 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}>
                    {loading ? (
                      <><span style={{ width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Account aanmaken...</>
                    ) : (
                      <>Gratis account aanmaken <ArrowRight size={17} /></>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
                  <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>of</span>
                  <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                </div>

                <button
                  data-testid="google-register-btn"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    padding: '12px 16px', borderRadius: '12px', border: '2px solid #e2e8f0', background: 'white',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4A017'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Registreren met Google
                </button>

                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', marginTop: '16px' }}>
                  Al een account? <Link to="/login" data-testid="register-account-login-link" style={{ color: '#D4A017', fontWeight: 600, textDecoration: 'none' }}>Inloggen</Link>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
